import React, { useState, useEffect } from 'react';
import './IntegrationsPage.scss';
import Title from '../../shared/components/title/Title';
import { InputSwitch } from 'primereact/inputswitch';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import Loader from '../../shared/components/common/loader/Loader';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { LookerStudioService } from '../../shared/services/integrations/LookerStudioService';
import { REACT_APP_GOOGLE_CLIENT_ID } from '../../shared/utils/config';
import { useNavigate } from 'react-router-dom';

const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const navigate = useNavigate();

  const [isLookerStudioConnected, setIsLookerStudioConnected] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);

  useEffect(() => {
    checkLookerStudioConnection();

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'LOOKER_STUDIO_CONNECTED') {
        checkLookerStudioConnection();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (disconnectSuccess) {
      const timer = setTimeout(() => {
        setDisconnectSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [disconnectSuccess]);

  const checkLookerStudioConnection = async () => {
    try {
      const status = await LookerStudioService.getConnectionStatus();
      setIsLookerStudioConnected(status.isConnected);
    } catch (error) {
      console.error('Error checking Looker Studio connection:', error);
    }
  };

  const handleConnectLookerStudio = async () => {
    const clientId = REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/looker-studio/callback`;
    const scopes = [
      'https://www.googleapis.com/auth/drive.file',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/datastudio'
    ].join(' ');

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${encodeURIComponent(scopes)}&response_type=code&access_type=offline&prompt=consent`;

    window.open(authUrl, '_blank', 'width=500,height=600');
  };

  const handleDisconnectLookerStudio = () => {
    confirmDialog({
      message: t('confirmDisconnectMessage'),
      header: t('confirmDisconnection'),
      defaultFocus: 'accept',
      accept: confirmDisconnect,
    });
  };

  const confirmDisconnect = async () => {
    setDisconnectError(null);
    setDisconnectSuccess(null);

    try {
      await LookerStudioService.disconnect();
      setIsLookerStudioConnected(false);
      setDisconnectSuccess(t('successfullyDisconnected'));
    } catch (error) {
      console.error('Error disconnecting:', error);
      setDisconnectError(t('failedToDisconnect'));
    }
  };

  const handleViewReports = () => {
    navigate('/periodic-reports?provider=LOOKER_STUDIO');
  };

  if (!accountResponse) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-content">
        <div className="page-box">
          <Title title={t('availableIntegrations')} />

          <div className="page-box__content">
            <div className="integration-page__content">
              <div className="integration-page__integration-box">
                <div className={`integration ${isLookerStudioConnected ? 'integration--active' : ''}`}>
                  <div className="integration__box">
                    <div className="integration__header">
                      <div className="integration__image integration__image--looker-studio" />
                      <InputSwitch
                        checked={isLookerStudioConnected}
                        disabled={true}
                        style={{
                          opacity: 1,
                          '--p-inputswitch-checked-bg': '#4285F4',
                          '--p-inputswitch-slider-bg': isLookerStudioConnected ? '#4285F4' : '#e9ecef'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="integration__text">
                      <div className="integration__title">{t('lookerStudio')}</div>
                      <div className="integration__subtitle">{t('lookerStudioDesc')}</div>
                    </div>
                  </div>
                  <div className="integration__content">
                    {t('lookerStudioIntegrationDesc')}

                    {disconnectError && (
                      <div className="integration__error-message">
                        {disconnectError}
                      </div>
                    )}

                    {disconnectSuccess && (
                      <div className="integration__success-message">
                        {disconnectSuccess}
                      </div>
                    )}

                    {isLookerStudioConnected && (
                      <div className="integration__actions">
                        <button
                          className="btn btn--sm btn--tertiary"
                          onClick={handleViewReports}
                          title={t('viewAllReports')}
                        >
                          <span className="ico--calendar ico--xs" style={{ marginRight: '0.5rem' }} />
                          {t('viewReports')}
                        </button>
                        <button
                          className="btn btn--sm btn--secondary"
                          onClick={handleDisconnectLookerStudio}
                        >
                          {t('disconnect')}
                        </button>
                      </div>
                    )}
                    {!isLookerStudioConnected && (
                      <div className="integration__actions">
                        <button
                          className="btn btn--sm"
                          onClick={handleConnectLookerStudio}
                        >
                          {t('connect')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog className="dialog-box" />
    </>
  );
};

export default IntegrationsPage;
